import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { PhoneCall, Loader2, Users, Layers, FileSignature, ListPlus, AlarmClock } from 'lucide-react';
import { PickList } from './PickList';
import { ClientSearch } from './ClientSearch';
import { CARD, INPUT, LABEL, Btn, cx } from '../contacts/ui';

const API = process.env.REACT_APP_BACKEND_URL;
const SOURCES = [
  ['portals', 'Active portals', 'Open client files', Layers],
  ['esign', 'E-Sign', 'Waiting to sign', FileSignature],
  ['callbacks', 'Callbacks', 'Due today', AlarmClock],
  ['custom', 'My own list', 'Hand-picked', ListPlus],
];

const SourceTabs = ({ source, setSource }) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="calling-sources">
    {SOURCES.map(([k, label, sub, Icon]) => (
      <button key={k} type="button" onClick={() => setSource(k)} data-testid={`calling-source-${k}`}
        className={cx('group rounded-2xl border px-3.5 py-3 text-left transition-[background-color,border-color,transform] duration-150 active:scale-[.98]',
          source === k ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--line)] bg-[var(--surface)] hover:bg-[var(--hover)]')}>
        <Icon className={cx('mb-2 h-4 w-4', source === k ? 'text-[var(--accent-ink)]' : 'text-[var(--muted)]')} />
        <span className="block text-sm font-bold text-[var(--ink)]">{label}</span>
        <span className="block text-xs text-[var(--muted)]">{sub}</span>
      </button>))}
  </div>
);

export const CallingSetup = ({ headers, onStarted, me, initialSource, callbacksDue }) => {
  const [opts, setOpts] = useState(null);
  const [members, setMembers] = useState([]);
  const [source, setSource] = useState(['esign', 'callbacks', 'custom'].includes(initialSource) ? initialSource : 'portals');
  const [segment, setSegment] = useState('all');
  const [status, setStatus] = useState('');
  const [member, setMember] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [added, setAdded] = useState([]);
  useEffect(() => { setPicked(new Set()); }, [preview]);
  const isIn = (k) => picked.has(k) || added.some((a) => a.key === k);
  const addClient = (r) => { if ((preview?.items || []).some((i) => i.key === r.key)) setPicked((p) => new Set(p).add(r.key)); else setAdded((a) => [...a, r]); };
  const removeClient = (k) => setAdded((a) => a.filter((x) => x.key !== k));
  const extras = added.filter((a) => !picked.has(a.key));
  const listed = source === 'callbacks' ? (callbacksDue || 0) : (preview?.count ? picked.size : 0);
  const total = listed + extras.length;
  const toggle = (k) => setPicked((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  useEffect(() => {
    axios.get(`${API}/api/calling/options`, { headers }).then((r) => setOpts(r.data)).catch(() => {});
    axios.get(`${API}/api/onboarding/team/directory`, { headers }).then((r) => {
      const all = (r.data.users || []).filter((u) => u.role !== 'leads_viewer' && u.is_active !== false);
      setMembers(['admin', 'manager', 'director'].includes(me?.role) ? all : all.filter((u) => u.id === r.data.me));
    }).catch(() => {});
  }, [headers, me]);

  const load = useCallback(() => {
    if (source === 'callbacks' || source === 'custom') { setPreview({ count: 0, items: [] }); return; }
    setPreview(null);
    axios.get(`${API}/api/calling/preview`, { headers, params: { source, segment, status: status || undefined, member: member || undefined } }).then((r) => setPreview(r.data)).catch(() => setPreview({ count: 0, items: [] }));
  }, [headers, source, segment, status, member]);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy(true);
    try {
      const body = source === 'callbacks' ? (callbacksDue ? { source: 'callbacks' } : { source: 'custom' }) : { source, segment, status: status || null, member: member || null, keys: [...picked] };
      const r = await axios.post(`${API}/api/calling/sessions`, { ...body, extra_ids: extras.map((a) => a.key) }, { headers }); onStarted(r.data);
    } catch (e) { setBusy(false); setPreview((p) => ({ ...(p || {}), error: e.response?.data?.detail || 'Could not start' })); }
  };
  const filters = source === 'portals' || source === 'esign';

  return (
    <div className="space-y-4" data-testid="calling-setup">
      <section className={cx(CARD, 'p-4 sm:p-5')}>
        <p className={LABEL}>Who do you want to call?</p>
        <SourceTabs source={source} setSource={setSource} />
        {source === 'portals' && <div className="mt-4 flex flex-wrap gap-1.5" data-testid="calling-segments">
          {Object.entries(opts?.segments || {}).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setSegment(k)} data-testid={`calling-segment-${k}`}
              className={cx('rounded-full px-3 py-1.5 text-xs font-semibold transition-colors', segment === k ? 'bg-[var(--solid)] text-[var(--on-solid)]' : 'bg-[var(--hover)] text-[var(--ink-2)] hover:text-[var(--ink)]')}>{label}</button>))}
        </div>}
        {filters && <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {source === 'portals' && <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="calling-filter-status" className={INPUT}>
            <option value="">Any status</option>
            {Object.entries(opts?.statuses || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>}
          <select value={member} onChange={(e) => setMember(e.target.value)} data-testid="calling-filter-member" className={INPUT}>
            <option value="">Any broker or team member</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>}
        {source === 'callbacks' && <p className="mt-4 rounded-xl bg-[var(--amber-bg)] px-3.5 py-2.5 text-sm text-[var(--amber-ink)]" data-testid="calling-callbacks-note">{callbacksDue ? `${callbacksDue} callback${callbacksDue === 1 ? '' : 's'} due today, overdue first.` : 'No callbacks due today. Add contacts below to call anyway.'}</p>}
        <ClientSearch headers={headers} added={added} onAdd={addClient} onRemove={removeClient} isIn={isIn} />
      </section>

      <section className={cx(CARD, 'p-4 sm:p-5')} data-testid="calling-preview">
        {!preview ? <p className="flex items-center gap-2 text-sm text-[var(--muted)]"><Loader2 className="h-4 w-4 animate-spin" /> Building your queue…</p> : (<>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-4xl font-bold tabular-nums tracking-tight text-[var(--ink)]" data-testid="calling-preview-count">{total}</p>
              <p className="text-sm text-[var(--muted)]"><Users className="mr-1 inline h-4 w-4" />{`${total === 1 ? 'person' : 'people'} to call`}{extras.length > 0 && source !== 'custom' ? ` · ${extras.length} added by you` : ''}{preview.no_mobile ? ` · ${preview.no_mobile} without a mobile` : ''}</p>
            </div>
            <Btn tone="green" size="lg" disabled={!total || busy} onClick={start} data-testid="calling-start-btn" className="fixed inset-x-3 bottom-4 z-30 shadow-[var(--shadow-md)] sm:static sm:inset-auto">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <PhoneCall className="h-5 w-5" />} Start calling
            </Btn>
          </div>
          {preview.error && <p className="mt-2 text-sm font-semibold text-[var(--red)]" data-testid="calling-start-error">{preview.error}</p>}
          {source === 'custom' && !total && <p className="mt-2 text-sm text-[var(--muted)]" data-testid="calling-custom-empty">Search above and add the people you want to call.</p>}
          {!!preview.items?.length && <PickList items={preview.items} picked={picked} onToggle={toggle} onAll={() => setPicked(new Set(preview.items.map((i) => i.key)))} onNone={() => setPicked(new Set())} />}
        </>)}
      </section>
    </div>
  );
};
